import React, { useMemo } from 'react';
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';

import { ITariff } from '../../../../../interfaces/tariff';
import { TariffsType } from '../../TariffList';
import { TariffEditFields } from '../../../../../store/ducks/tariff.duck';
import TopTableCell from '../../../../../components/other/Table/TopTableCell';
import { useFormatMessage } from '../../../../../hooks';
import EditableCellTariff from '../EditableCellTariff';
import EditableCellTariffFee from '../EditableCellTariffFee';

const useStyles = makeStyles((theme: any) => ({
  table: {
    flex: 1,
    backgroundColor: '#fff',
    overflowX: 'auto',
  },
  blackCell: {
    backgroundColor: 'rgba(150, 150, 150, 0.4)',
  },
  grayCell: {
    backgroundColor: 'rgba(150, 150, 150, 0.2)',
  },
  titleCell: {
    width: '300px',
  },
  freeCell: {
    width: '200px',
    backgroundColor: '#F7F5F0',
  },
  proCell: {
    backgroundColor: theme.palette.success.dark,
    color: '#fff',
  },
  tariffCell: {
    width: '200px',
  },
  button: {
    width: '100%',
  },
  freeButtonCell: {
    width: '200px',
    backgroundColor: 'rgba(150, 150, 150, 0.2)',
  },
  buyerProCell: {
    width: '200px',
    backgroundColor: theme.palette.secondary.main,
  },
  providerProCell: {
    width: '200px',
    backgroundColor: theme.palette.primary.main,
    color: '#00000',
  },
}));

const TariffTable: React.FC<{
  type: TariffsType;
  editable: boolean;
  tariffs: ITariff[];
  loading: boolean;
  success: boolean;
  editTariff: (
    id: number,
    forRole: string,
    name: string,
    field: TariffEditFields,
    value: string | number | null
  ) => void;
  submit: (
    id: number,
    field: TariffEditFields,
    value:
      | string
      | number
      | {
          [key: string]: number;
        }
      | null
  ) => void;
  setModal: (value: boolean) => void;
  canPay: boolean;
  disablePay: boolean;
}> = ({
  type,
  tariffs,
  loading,
  success,
  submit,
  editTariff,
  editable,
  setModal,
  canPay,
  disablePay,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const fm = useFormatMessage();
  const classes = useStyles();
  const baseTarriff = useMemo(() => tariffs.find(i => i.id === 5), [tariffs]);
  const proTarriff = useMemo(() => tariffs.find(i => i.id === 6), [tariffs]);
  const proClass =
    type === TariffsType.buyers ? classes.buyerProCell : classes.providerProCell;

  const handleSumbitDistance = ({
    tariff,
    field,
    value,
    index,
  }: {
    tariff: ITariff;
    field: 'key' | 'value';
    value: number | string;
    index: number;
  }) => {
    const distanceFee = { ...(tariff.distance_fee || {}) };
    if (
      field === 'value' &&
      (!Object.keys(distanceFee)?.[index] || Object.keys(distanceFee)?.[index] === '0')
    ) {
      enqueueSnackbar('Укажите расстояние', {
        variant: 'error',
      });
      return;
    }
    const newArr = [
      [
        // eslint-disable-next-line no-nested-ternary
        index === 0 && field === 'key'
          ? typeof value === 'string'
            ? parseInt(value, 10).toString()
            : String(value)
          : Object.keys(distanceFee)?.[0] || '0',
        index === 0 && field === 'value'
          ? Number(value)
          : Object.values(distanceFee)?.[0] || 0,
      ],
      [
        // eslint-disable-next-line no-nested-ternary
        index === 1 && field === 'key'
          ? typeof value === 'string'
            ? parseInt(value, 10).toString()
            : String(value)
          : Object.keys(distanceFee)?.[1] || '0',
        index === 1 && field === 'value'
          ? Number(value)
          : Object.values(distanceFee)?.[1] || 0,
      ],
      [
        // eslint-disable-next-line no-nested-ternary
        index === 2 && field === 'key'
          ? typeof value === 'string'
            ? parseInt(value, 10).toString()
            : String(value)
          : Object.keys(distanceFee)?.[2] || '0',
        index === 2 && field === 'value'
          ? Number(value)
          : Object.values(distanceFee)?.[2] || 0,
      ],
      [
        // eslint-disable-next-line no-nested-ternary
        index === 3 && field === 'key'
          ? typeof value === 'string'
            ? parseInt(value, 10).toString()
            : String(value)
          : Object.keys(distanceFee)?.[3] || '0',
        index === 3 && field === 'value'
          ? Number(value)
          : Object.values(distanceFee)?.[3] || 0,
      ],
      [
        // eslint-disable-next-line no-nested-ternary
        index === 4 && field === 'key'
          ? typeof value === 'string'
            ? parseInt(value, 10).toString()
            : String(value)
          : Object.keys(distanceFee)?.[4] || '0',
        index === 4 && field === 'value'
          ? Number(value)
          : Object.values(distanceFee)?.[4] || 0,
      ],
    ];
    const newTariff = Object.fromEntries(newArr.filter(i => i[0] !== '0'));
    submit(tariff.id, 'distanceFee', newTariff);
  };

  if (!baseTarriff && !proTarriff) return <></>;

  return (
    <Card className={classes.table}>
      <CardContent>
        <Table>
          <TableHead>
            <TableRow>
              <TopTableCell> </TopTableCell>
              {baseTarriff && (
                <TopTableCell style={{ color: 'white', fontWeight: 'bold' }} align='center'>
                  {baseTarriff?.name || ''}
                </TopTableCell>
              )}
              {proTarriff && (
                <TopTableCell style={{ color: 'white', fontWeight: 'bold' }} align='center'>
                  {proTarriff?.name || ''}
                </TopTableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            <TableRow>
              <TableCell className={classes.titleCell}>
                <b>{fm('PRICE.KM')}</b>
              </TableCell>
              {baseTarriff && (
                <TableCell align='center' className={classes.freeCell}>
                  <EditableCellTariff
                    tariff={baseTarriff}
                    loading={loading}
                    success={success}
                    submit={submit}
                    editable={editable}
                    field='kilometerPrice'
                    fieldValue='kilometer_price'
                  />
                </TableCell>
              )}
              {proTarriff && (
                <TableCell align='center' className={proClass}>
                  <EditableCellTariff
                    tariff={proTarriff}
                    loading={loading}
                    success={success}
                    submit={submit}
                    editable={editable}
                    field='kilometerPrice'
                    fieldValue='kilometer_price'
                  />
                </TableCell>
              )}
            </TableRow>

            <TableRow>
              <TableCell className={classes.titleCell}>
                <b>{fm('MINIMUM.KM')}</b>
              </TableCell>
              {baseTarriff && (
                <TableCell align='center' className={classes.freeCell}>
                  <EditableCellTariff
                    tariff={baseTarriff}
                    loading={loading}
                    success={success}
                    submit={submit}
                    editable={editable}
                    field='minimumKm'
                    fieldValue='minimum_km'
                  />
                </TableCell>
              )}
              {proTarriff && (
                <TableCell align='center' className={proClass}>
                  <EditableCellTariff
                    tariff={proTarriff}
                    loading={loading}
                    success={success}
                    submit={submit}
                    editable={editable}
                    field='minimumKm'
                    fieldValue='minimum_km'
                  />
                </TableCell>
              )}
            </TableRow>

            <TableRow>
              <TableCell className={classes.titleCell}>
                <b>{fm('PRICE.MINUTE.DAY')}</b>
              </TableCell>
              {baseTarriff && (
                <TableCell align='center' className={classes.freeCell}>
                  <EditableCellTariff
                    tariff={baseTarriff}
                    loading={loading}
                    success={success}
                    submit={submit}
                    editable={editable}
                    field='daytimeMinutePrice'
                    fieldValue='daytime_minute_price'
                  />
                </TableCell>
              )}
              {proTarriff && (
                <TableCell align='center' className={proClass}>
                  <EditableCellTariff
                    tariff={proTarriff}
                    loading={loading}
                    success={success}
                    submit={submit}
                    editable={editable}
                    field='daytimeMinutePrice'
                    fieldValue='daytime_minute_price'
                  />
                </TableCell>
              )}
            </TableRow>

            <TableRow>
              <TableCell className={classes.titleCell}>
                <b>{fm('PRICE.MINUTE.NIGHT')}</b>
              </TableCell>
              {baseTarriff && (
                <TableCell align='center' className={classes.freeCell}>
                  <EditableCellTariff
                    tariff={baseTarriff}
                    loading={loading}
                    success={success}
                    submit={submit}
                    editable={editable}
                    field='nighttimeMinutePrice'
                    fieldValue='nighttime_minute_price'
                  />
                </TableCell>
              )}
              {proTarriff && (
                <TableCell align='center' className={proClass}>
                  <EditableCellTariff
                    tariff={proTarriff}
                    loading={loading}
                    success={success}
                    submit={submit}
                    editable={editable}
                    field='nighttimeMinutePrice'
                    fieldValue='nighttime_minute_price'
                  />
                </TableCell>
              )}
            </TableRow>

            <TableRow>
              <TableCell className={classes.titleCell}>
                <b>{fm('MINIMUM.TIME')}</b>
              </TableCell>
              {baseTarriff && (
                <TableCell align='center' className={classes.freeCell}>
                  <EditableCellTariff
                    tariff={baseTarriff}
                    loading={loading}
                    success={success}
                    submit={submit}
                    editable={editable}
                    field='minimumTime'
                    fieldValue='minimum_time'
                  />
                </TableCell>
              )}
              {proTarriff && (
                <TableCell align='center' className={proClass}>
                  <EditableCellTariff
                    tariff={proTarriff}
                    loading={loading}
                    success={success}
                    submit={submit}
                    editable={editable}
                    field='minimumTime'
                    fieldValue='minimum_time'
                  />
                </TableCell>
              )}
            </TableRow>

            <TableRow>
              <TableCell className={classes.titleCell}>
                <b>{fm('COMMISSION.PERCENT')}</b>
              </TableCell>
              {baseTarriff && (
                <TableCell align='center' className={classes.freeCell}>
                  <EditableCellTariff
                    tariff={baseTarriff}
                    loading={loading}
                    success={success}
                    submit={submit}
                    editable={editable}
                    field='commissionPercent'
                    fieldValue='commission_percent'
                  />
                </TableCell>
              )}
              {proTarriff && (
                <TableCell align='center' className={proClass}>
                  <EditableCellTariff
                    tariff={proTarriff}
                    loading={loading}
                    success={success}
                    submit={submit}
                    editable={editable}
                    field='commissionPercent'
                    fieldValue='commission_percent'
                  />
                </TableCell>
              )}
            </TableRow>
            <TableRow>
              <TableCell className={classes.titleCell}>
                <b>Наценка за смену населенного пункта в %</b>
              </TableCell>
              {baseTarriff && (
                <TableCell align='center' className={classes.freeCell}>
                  <EditableCellTariff
                    tariff={baseTarriff}
                    loading={loading}
                    success={success}
                    submit={submit}
                    editable={editable}
                    field='provinceFee'
                    fieldValue='province_fee'
                  />
                </TableCell>
              )}
              {proTarriff && (
                <TableCell align='center' className={proClass}>
                  <EditableCellTariff
                    tariff={proTarriff}
                    loading={loading}
                    success={success}
                    submit={submit}
                    editable={editable}
                    field='provinceFee'
                    fieldValue='province_fee'
                  />
                </TableCell>
              )}
            </TableRow>
            <TableRow>
              <TableCell className={classes.titleCell}>
                <b>Наценка за ночные поездки, %</b>
              </TableCell>
              {baseTarriff && (
                <TableCell align='center' className={classes.freeCell}>
                  <EditableCellTariff
                    tariff={baseTarriff}
                    loading={loading}
                    success={success}
                    submit={submit}
                    editable={editable}
                    field='nightDriveFee'
                    fieldValue='night_drive_fee'
                  />
                </TableCell>
              )}
              {proTarriff && (
                <TableCell align='center' className={proClass}>
                  <EditableCellTariff
                    tariff={proTarriff}
                    loading={loading}
                    success={success}
                    submit={submit}
                    editable={editable}
                    field='nightDriveFee'
                    fieldValue='night_drive_fee'
                  />
                </TableCell>
              )}
            </TableRow>
            <TableRow>
              <TableCell className={classes.titleCell}>
                <b>Первая наценка за расстояние км / %</b>
              </TableCell>
              {baseTarriff && (
                <TableCell align='center' className={classes.freeCell}>
                  <EditableCellTariffFee
                    tariff={baseTarriff}
                    loading={loading}
                    success={success}
                    submit={handleSumbitDistance}
                    editable={editable}
                    field='distanceFee'
                    fieldValue='distance_fee'
                    index={0}
                  />
                </TableCell>
              )}
              {proTarriff && (
                <TableCell align='center' className={proClass}>
                  <EditableCellTariffFee
                    tariff={proTarriff}
                    loading={loading}
                    success={success}
                    submit={handleSumbitDistance}
                    editable={editable}
                    field='distanceFee'
                    fieldValue='distance_fee'
                    index={0}
                  />
                </TableCell>
              )}
            </TableRow>
            <TableRow>
              <TableCell className={classes.titleCell}>
                <b>Вторая наценка за расстояние км / %</b>
              </TableCell>
              {baseTarriff && (
                <TableCell align='center' className={classes.freeCell}>
                  <EditableCellTariffFee
                    tariff={baseTarriff}
                    loading={loading}
                    success={success}
                    submit={handleSumbitDistance}
                    editable={editable}
                    field='distanceFee'
                    fieldValue='distance_fee'
                    index={1}
                  />
                </TableCell>
              )}
              {proTarriff && (
                <TableCell align='center' className={proClass}>
                  <EditableCellTariffFee
                    tariff={proTarriff}
                    loading={loading}
                    success={success}
                    submit={handleSumbitDistance}
                    editable={editable}
                    field='distanceFee'
                    fieldValue='distance_fee'
                    index={1}
                  />
                </TableCell>
              )}
            </TableRow>
            <TableRow>
              <TableCell className={classes.titleCell}>
                <b>Третья наценка за расстояние км / %</b>
              </TableCell>
              {baseTarriff && (
                <TableCell align='center' className={classes.freeCell}>
                  <EditableCellTariffFee
                    tariff={baseTarriff}
                    loading={loading}
                    success={success}
                    submit={handleSumbitDistance}
                    editable={editable}
                    field='distanceFee'
                    fieldValue='distance_fee'
                    index={2}
                  />
                </TableCell>
              )}
              {proTarriff && (
                <TableCell align='center' className={proClass}>
                  <EditableCellTariffFee
                    tariff={proTarriff}
                    loading={loading}
                    success={success}
                    submit={handleSumbitDistance}
                    editable={editable}
                    field='distanceFee'
                    fieldValue='distance_fee'
                    index={2}
                  />
                </TableCell>
              )}
            </TableRow>
            <TableRow>
              <TableCell className={classes.titleCell}>
                <b>Четвертая наценка за расстояние км / %</b>
              </TableCell>
              {baseTarriff && (
                <TableCell align='center' className={classes.freeCell}>
                  <EditableCellTariffFee
                    tariff={baseTarriff}
                    loading={loading}
                    success={success}
                    submit={handleSumbitDistance}
                    editable={editable}
                    field='distanceFee'
                    fieldValue='distance_fee'
                    index={3}
                  />
                </TableCell>
              )}
              {proTarriff && (
                <TableCell align='center' className={proClass}>
                  <EditableCellTariffFee
                    tariff={proTarriff}
                    loading={loading}
                    success={success}
                    submit={handleSumbitDistance}
                    editable={editable}
                    field='distanceFee'
                    fieldValue='distance_fee'
                    index={3}
                  />
                </TableCell>
              )}
            </TableRow>
            <TableRow>
              <TableCell className={classes.titleCell}>
                <b>Пятая наценка за расстояние км / %</b>
              </TableCell>
              {baseTarriff && (
                <TableCell align='center' className={classes.freeCell}>
                  <EditableCellTariffFee
                    tariff={baseTarriff}
                    loading={loading}
                    success={success}
                    submit={handleSumbitDistance}
                    editable={editable}
                    field='distanceFee'
                    fieldValue='distance_fee'
                    index={4}
                  />
                </TableCell>
              )}
              {proTarriff && (
                <TableCell align='center' className={proClass}>
                  <EditableCellTariffFee
                    tariff={proTarriff}
                    loading={loading}
                    success={success}
                    submit={handleSumbitDistance}
                    editable={editable}
                    field='distanceFee'
                    fieldValue='distance_fee'
                    index={4}
                  />
                </TableCell>
              )}
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default TariffTable;
